import * as React from "react";

import { cn } from "utils/cn";
import { useFormFieldControl } from "./FormField";

/** Shared look of every text-like control (Input, Textarea, Select trigger). */
export const controlClasses = cn(
  "w-full rounded-control border border-input bg-surface text-sm text-foreground shadow-xs",
  "transition-[border-color,box-shadow] duration-fast ease-standard placeholder:text-subtle-foreground",
  "focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/15",
  "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-70",
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger/15"
);

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Icon or text inside the left edge (non-interactive). */
  leading?: React.ReactNode;
  /** Element inside the right edge, e.g. a clear or visibility button. */
  trailing?: React.ReactNode;
  /** Classes for the outer wrapper (layout); `className` styles the <input>. */
  wrapperClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, wrapperClassName, leading, trailing, type = "text", ...rest }, ref) => {
    const props = useFormFieldControl(rest);
    return (
      <div data-ui="input" className={cn("relative flex items-center", wrapperClassName)}>
        {leading && (
          <span className="pointer-events-none absolute left-3 flex items-center text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
            {leading}
          </span>
        )}
        <input
          ref={ref}
          type={type}
          className={cn(
            controlClasses,
            "h-10 px-3",
            leading && "pl-9",
            trailing && "pr-10",
            className
          )}
          {...props}
        />
        {trailing && <span className="absolute right-1 flex items-center">{trailing}</span>}
      </div>
    );
  }
);
Input.displayName = "Input";

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 4, ...rest }, ref) => {
    const props = useFormFieldControl(rest);
    return (
      <textarea
        ref={ref}
        data-ui="textarea"
        rows={rows}
        className={cn(controlClasses, "min-h-[5rem] px-3 py-2 leading-relaxed", className)}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";
